import { Component } from 'react'

import type { ReactNode, ErrorInfo } from 'react'

interface SectionErrorBoundaryProps {
  children: ReactNode
  /** Section name for error logging */
  sectionName: string
  /** Custom error message */
  errorMessage?: string
  /** Callback when error is caught */
  onError?: (error: Error, errorInfo: ErrorInfo) => void
}

interface SectionErrorBoundaryState {
  hasError: boolean
  error: Error | null
}

/**
 * SectionErrorBoundary - Lightweight error boundary for homepage sections
 *
 * Catches errors in individual sections (BannerSlider, ProductSlider, PromoBlock)
 * without breaking the entire homepage. Shows a minimal fallback UI.
 *
 * @example
 * <SectionErrorBoundary sectionName="BannerSlider" errorMessage={LABELS.BANNER_SLIDER_ERROR}>
 *   <BannerSlider banners={banners} />
 * </SectionErrorBoundary>
 */
export class SectionErrorBoundary extends Component<SectionErrorBoundaryProps, SectionErrorBoundaryState> {
  constructor(props: SectionErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): SectionErrorBoundaryState {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log error with section context
    // eslint-disable-next-line no-console -- Error logging is intentional for debugging
    console.error(`[${this.props.sectionName}] Error:`, error, errorInfo)
    this.props.onError?.(error, errorInfo)
  }

  render(): ReactNode {
    if (this.state.hasError) {
      // Minimal fallback - section just disappears with a subtle message
      // This prevents one broken section from breaking the entire homepage
      return (
        <div className="py-4 text-center">
          <p className="text-gray-400 text-sm">
            {this.props.errorMessage ?? 'Eroare la încărcarea secțiunii.'}
          </p>
        </div>
      )
    }

    return this.props.children
  }
}
